// renderConfig — non-component shared module for the detail-view render engine.
// Holds the Admin-facing option lists and the file/document resolution helpers,
// kept out of the .jsx engine so React Fast Refresh stays happy and so other
// modules (DocumentLink, the admin screen) can reuse them.

import { getDefaults } from '../../services/detailDefaults';

// ── Admin option lists ───────────────────────────────────────────────────────
export const RENDER_TYPES = [
  { value: 'auto', label: 'Default (auto)' },
  { value: 'text', label: 'Text' },
  { value: 'tag', label: 'Tag' },
  { value: 'tags', label: 'Multi Tag (Skills)' },
  { value: 'badge', label: 'Badge' },
  { value: 'chip', label: 'Chip' },
  { value: 'document', label: 'Document' },
  { value: 'link', label: 'Link' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'currency', label: 'Currency' },
  { value: 'budget', label: 'Budget' },
  { value: 'html', label: 'HTML' },
];

export const DOCUMENT_MODES = [
  { value: 'preview', label: 'Preview (viewer)' },
  { value: 'download', label: 'Download button' },
  { value: 'link', label: 'Link (filename)' },
  { value: 'tag', label: 'Tag' },
  { value: 'text', label: 'Text (filename)' },
];

export const TEXT_TRANSFORMS = [
  { value: 'none', label: 'None' },
  { value: 'upper', label: 'UPPERCASE' },
  { value: 'lower', label: 'lowercase' },
  { value: 'title', label: 'Title Case' },
];

// ── file / document resolution ───────────────────────────────────────────────
const LOC_EXACT = ['location', 'path', 'url', 'key'];
const LOC_SUFFIX = ['location', 'filepath', 'path', 'url', 'key'];
const NAME_SUFFIX = ['uploadedfilename', 'uploadname', 'originalname', 'displayname', 'documentname'];
const NAME_LAST = ['name'];
const lc = (s) => String(s).toLowerCase();
const isEmpty = (v) => v === undefined || v === null || v === '';

function pickExact(obj, keys) {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v) return v;
  }
  return '';
}

function pickSuffix(obj, suffixes) {
  for (const suffix of suffixes) {
    for (const [key, val] of Object.entries(obj)) {
      if (typeof val === 'string' && val && lc(key).endsWith(suffix)) return val;
    }
  }
  return '';
}

export function fileNameFromPath(path) {
  if (!path) return '';
  const clean = String(path).split('?')[0].split('#')[0];
  return decodeURIComponent(clean.split('/').pop() || '');
}

export function buildFileUrl(location) {
  if (!location) return '';
  const loc = String(location);
  if (/^https?:\/\//i.test(loc)) return loc;
  const base = getDefaults().s3BaseUrl || '';
  return base ? `${base}/${loc.replace(/^\/+/, '')}` : '';
}

export function locationOf(doc) {
  if (typeof doc === 'string') return doc.includes('/') ? doc : '';
  if (!doc || typeof doc !== 'object') return '';
  return pickExact(doc, LOC_EXACT) || pickSuffix(doc, LOC_SUFFIX);
}

export function docDisplayName(doc) {
  if (!doc) return 'Document';
  if (typeof doc === 'string') return fileNameFromPath(doc) || doc;
  if (typeof doc !== 'object') return 'Document';
  const order = getDefaults().documentNameOrder ?? [];
  return (
    pickExact(doc, order) ||
    pickSuffix(doc, NAME_SUFFIX) ||
    fileNameFromPath(locationOf(doc)) ||
    pickSuffix(doc, NAME_LAST) ||
    'Document'
  );
}

// extractDocuments flattens any file-field value (array | object | string) into
// a list of { location, name, url }.
export function extractDocuments(value) {
  if (isEmpty(value)) return [];
  const items = Array.isArray(value) ? value : [value];
  const out = [];
  for (const item of items) {
    if (isEmpty(item)) continue;
    if (typeof item === 'string') {
      const location = item.includes('/') ? item : '';
      out.push({ location, name: fileNameFromPath(item) || item, url: buildFileUrl(location) });
    } else if (typeof item === 'object') {
      const location = locationOf(item);
      out.push({ location, name: docDisplayName(item), url: buildFileUrl(location) });
    }
  }
  return out;
}
